// Tipos para el sistema de insumos/ingredientes

export type IngredientUnit = 
  | 'unidad'
  | 'kg' 
  | 'gr' 
  | 'lt' 
  | 'ml' 
  | 'docena'
  | 'caja'
  | 'paquete'
  | 'lata'
  | 'botella'
  | 'bolsa'
  | 'hojas';

// Stock por localización
export interface LocationStock {
  locationId: string;
  locationName: string;
  currentStock: number;
  lastRestockDate?: string;
  expirationDate?: string;
  isActive: boolean;
}

// Proveedor básico
export interface Supplier {
  id: string;
  businessName: string;
  ruc: string;
  email?: string;
  phone?: string;
  address: {
    street: string;
    city: string;
    region: string;
    postalCode?: string;
    country: string;
  };
  primaryContact: {
    name?: string;
    email?: string;
    phone?: string;
    position?: string;
  };
  isActive: boolean;
  rating?: number;
  createdAt: string;
  updatedAt: string;
}

// Relación proveedor-ingrediente
export interface SupplierIngredient {
  id: string;
  supplierId: string;
  ingredientId: string;
  ingredientName: string;
  category: string;
  unit: string;
  unitPrice: number;
  minimumOrder?: number;
  isActive: boolean;
  isPreferred: boolean;
  lastUpdated: string;
  supplier?: Supplier; // Populated when needed
}

// Historial de precios por proveedor
export interface PriceHistory {
  id: string;
  ingredientId: string;
  supplierId?: string;
  price: number;
  effectiveDate: string;
  endDate?: string;
  reason?: string;
  userId?: string;
  userName?: string;
  createdAt: string;
  supplier?: Supplier; // Populated when needed
}

export type ListDestination = 'LOCAL' | 'PRINCIPAL' | 'AMBOS' | 'NO';

export interface Ingredient {
  id: string;
  name: string;
  description?: string;
  category: IngredientCategory;
  categoryName?: string; // Nombre de la categoría desde el join
  categoryDescription?: string; // Descripción de la categoría desde el join
  unit: IngredientUnit;
  unitId?: string; // ID de la unidad
  unitName?: string; // Nombre de la unidad desde el join
  unitDisplayName?: string; // Nombre de display de la unidad desde el join
  unitSymbol?: string; // Símbolo de la unidad desde el join
  supplier?: string; // Legacy field for compatibility
  // Configuración global del ingrediente
  globalCostPerUnit?: number; // Costo base del proveedor (legacy)
  // Configuración de destino y despacho
  listDestination?: ListDestination; // Si va al local, local principal o ambos
  dispatchMultiple?: number; // Múltiplo de despacho para transferencias
  purchaseDestination?: ListDestination; // Destino de compra: local, principal o ambos
  trackInventory?: boolean; // Si se realiza inventario para este insumo
  // Proveedores múltiples
  suppliers?: SupplierIngredient[]; // Lista de proveedores para este ingrediente
  priceHistory?: PriceHistory[]; // Historial de precios
  // Stock por localización
  locationStocks: LocationStock[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;

  // Campos calculados para compatibilidad (basados en el local actual)
  currentStock?: number;
  location?: string;
  lastRestockDate?: string;
  expirationDate?: string;
}

// IngredientCategory is now a string to support dynamic categories from the database
export type IngredientCategory = string;

export interface RecipeIngredient {
  ingredientId: string;
  ingredient?: Ingredient; // Populated when needed
  quantity: number;
  unit: IngredientUnit;
  notes?: string; // e.g., "cortado en rodajas"
}

export interface ProductModifier {
  id: string;
  name: string;
  ingredientId: string;
  ingredient?: Ingredient;
  action: ModifierAction;
  quantity: number;
  extraCost: number;
  isDefault: boolean;
}

export type ModifierAction = 'add' | 'remove' | 'extra' | 'less';

export interface ModifierGroup {
  id: string;
  name: string; // e.g., "Vegetales", "Salsas", "Extras"
  modifiers: ProductModifier[];
  minSelection: number;
  maxSelection: number;
  isRequired: boolean;
}

export interface IngredientMovement {
  id: string;
  ingredientId: string;
  ingredient?: Ingredient;
  type: MovementType;
  quantity: number;
  unit: IngredientUnit;
  previousStock: number;
  newStock: number;
  reason: string;
  reference?: string; // e.g., saleId, purchaseOrderId
  performedBy: string;
  performedAt: string;
}

export type MovementType = 
  | 'purchase'      // Compra
  | 'sale'          // Venta (descuento por receta)
  | 'adjustment'    // Ajuste manual
  | 'waste'         // Merma
  | 'return'        // Devolución
  | 'transfer';     // Transferencia entre locales

export interface StockAlert {
  id: string;
  ingredientId: string;
  ingredient: Ingredient;
  type: 'low_stock' | 'out_of_stock' | 'expiring_soon' | 'expired';
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  createdAt: string;
  acknowledged: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
}

export interface PurchaseOrderItem {
  ingredientId: string;
  ingredient?: Ingredient;
  quantity: number;
  unit: IngredientUnit;
  unitCost: number;
  totalCost: number;
}

export interface PurchaseOrder {
  id: string;
  orderNumber: string;
  supplier: string;
  items: PurchaseOrderItem[];
  subtotal: number;
  tax: number;
  total: number;
  status: 'draft' | 'sent' | 'received' | 'cancelled';
  orderDate: string;
  expectedDate?: string;
  receivedDate?: string;
  notes?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// Helpers para cálculos
export interface IngredientCostCalculation {
  ingredientId: string;
  quantity: number;
  unit: IngredientUnit;
  unitCost: number;
  totalCost: number;
}

export interface RecipeCostCalculation {
  ingredients: IngredientCostCalculation[];
  totalCost: number;
  suggestedPrice: number; // Based on margin
  margin: number; // Percentage
}

// Helper functions para trabajar con ingredientes multi-local
export const getLocationStock = (ingredient: Ingredient, locationId: string): LocationStock | undefined => {
  return ingredient.locationStocks.find(stock => stock.locationId === locationId);
};

export const getIngredientForLocation = (ingredient: Ingredient, locationId: string): Ingredient => {
  const locationStock = getLocationStock(ingredient, locationId);

  return {
    ...ingredient,
    currentStock: locationStock?.currentStock || 0,
    location: locationStock?.locationName || '',
    lastRestockDate: locationStock?.lastRestockDate,
    expirationDate: locationStock?.expirationDate,
  };
};

export const createLocationStock = (locationId: string, locationName: string): LocationStock => {
  return {
    locationId,
    locationName,
    currentStock: 0,
    isActive: true,
  };
};

// Helper functions para proveedores múltiples

export const getPreferredSupplier = (ingredient: Ingredient): SupplierIngredient | undefined => {
  return ingredient.suppliers?.find(supplier => supplier.isPreferred && supplier.isActive);
};

export const getActiveSuppliers = (ingredient: Ingredient): SupplierIngredient[] => {
  return ingredient.suppliers?.filter(supplier => supplier.isActive) || [];
};

export const getCurrentPrice = (supplierIngredient: SupplierIngredient, priceHistory?: PriceHistory[]): number => {
  if (!priceHistory) return supplierIngredient.unitPrice;

  const today = new Date().toISOString().split('T')[0];
  const currentPrice = priceHistory
    .filter(p =>
      p.supplierId === supplierIngredient.supplierId &&
      p.effectiveDate <= today &&
      (!p.endDate || p.endDate >= today)
    )
    .sort((a, b) => new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime())[0];

  return currentPrice?.price || supplierIngredient.unitPrice;
};

export const getPriceRange = (ingredient: Ingredient): { min: number; max: number; display: string } => {
  const activeSuppliers = getActiveSuppliers(ingredient);

  if (activeSuppliers.length === 0) {
    return { min: 0, max: 0, display: 'Sin proveedores' };
  }

  const prices = activeSuppliers.map(supplier => getCurrentPrice(supplier, ingredient.priceHistory));
  const min = Math.min(...prices);
  const max = Math.max(...prices);

  if (min === max) {
    return { min, max, display: `S/ ${min.toFixed(2)}` };
  }

  return { min, max, display: `S/ ${min.toFixed(2)} - S/ ${max.toFixed(2)}` };
};

export const getDisplayPrice = (ingredient: Ingredient): string => {
  const preferred = getPreferredSupplier(ingredient);
  if (preferred) {
    const price = getCurrentPrice(preferred, ingredient.priceHistory);
    return `S/ ${price.toFixed(2)} (${preferred.supplier?.businessName || 'Preferido'})`;
  }

  return getPriceRange(ingredient).display;
};